/**
 * Numbers, in Beam's house style.
 *
 * Taken verbatim from `Guide to report abbreviations`, the QA training document
 * in the template library:
 *
 *     Numbers (1-9)        one, nine
 *     Numbers (10 & above) 10, 11, 12
 *     Degrees              50 degrees uses the symbol, 50 followed by U+00B0
 *     Distance and weight  50km, 10cm, 100mg, 7.5kg
 *     Time                 7 and a half years renders as 7 with a vulgar half,
 *                          seven years old, 10 years old
 *     Duration             3-6 months, one month, 12 months
 *     Fractions            5/10, 6-7/10
 *
 * So the rule is not "spell small numbers". It is "spell small numbers when
 * they are counting something in prose", and leave them alone when they are a
 * measurement, a range, a fraction, a score, a date or a label. The exclusions
 * below are what make that distinction, and they matter more than the rule.
 */
const UNITS_TO_NINETEEN = [
    'zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine',
    'ten', 'eleven', 'twelve', 'thirteen', 'fourteen', 'fifteen', 'sixteen',
    'seventeen', 'eighteen', 'nineteen',
];
const TENS = {
    twenty: 20, thirty: 30, forty: 40, fifty: 50,
    sixty: 60, seventy: 70, eighty: 80, ninety: 90,
};
export const DIGIT_WORD = {
    '1': 'one', '2': 'two', '3': 'three', '4': 'four', '5': 'five',
    '6': 'six', '7': 'seven', '8': 'eight', '9': 'nine',
};
export const WORD_VALUE = new Map([
    ...UNITS_TO_NINETEEN.map((w, i) => [w, i]),
    ...Object.entries(TENS),
]);
/** Vulgar fractions Beam's guide uses. */
export const VULGAR = {
    '1/2': '½',
    '1/4': '¼',
    '3/4': '¾',
    '1/3': '⅓',
    '2/3': '⅔',
};
const FRACTION_WORDS = {
    half: '½',
    quarter: '¼',
    third: '⅓',
    'three quarters': '¾',
    'two thirds': '⅔',
};
/**
 * Characters that, immediately before or after a digit, mean the digit is part
 * of something the house rule does not touch: another number, a decimal, a
 * fraction, a range, a score, a percentage, a currency amount, a degree, a
 * time, or a legal or clause reference.
 */
const NUMERIC_NEIGHBOUR = /[\d.,:/\-\u2013\u2014%$°+*^#]/;
/**
 * Words before a digit that make it a label rather than a count. `Question 3`
 * and `page 4` keep their numerals; `3 children` does not.
 */
const LABEL_BEFORE = new Set([
    'question', 'questions', 'q', 'part', 'parts', 'item', 'items', 'section',
    'sections', 'clause', 'clauses', 'paragraph', 'paragraphs', 'page', 'pages',
    'figure', 'figures', 'table', 'tables', 'appendix', 'attachment', 'annexure',
    'schedule', 'chapter', 'volume', 'form', 'apia', 'ams', 'grade', 'stage',
    'class', 'type', 'level', 'phase', 'step', 'edition', 'version', 'no', 'number',
    'chapter', 'tier', 'band', 'score', 'scale', 'category', 'group', 'code',
]);
/**
 * Words after a digit that make it a measurement or a label. Units are handled
 * by the measures pass, which claims the span first; this list catches the rest.
 */
const LABEL_AFTER = new Set([
    'out', 'of', 'to', 'v', 'vs',
]);
function wordBefore(text, start) {
    let i = start - 1;
    while (i >= 0 && /\s/.test(text[i]))
        i--;
    const wordEnd = i + 1;
    while (i >= 0 && /[A-Za-z'-]/.test(text[i]))
        i--;
    return text.slice(i + 1, wordEnd).toLowerCase();
}
function wordAfter(text, end) {
    let i = end;
    while (i < text.length && /\s/.test(text[i]))
        i++;
    const wordStart = i;
    while (i < text.length && /[A-Za-z'-]/.test(text[i]))
        i++;
    return text.slice(wordStart, i).toLowerCase();
}
/**
 * Should a bare digit 1 to 9 be written as a word?
 *
 * Returns a reason string when the answer is no, which the caller can surface.
 */
export function digitStaysNumeric(ctx) {
    const { text, start, end } = ctx;
    const prevChar = start > 0 ? text[start - 1] : '';
    const nextChar = end < text.length ? text[end] : '';
    if (prevChar && NUMERIC_NEIGHBOUR.test(prevChar))
        return 'part of a larger numeric expression';
    if (nextChar && NUMERIC_NEIGHBOUR.test(nextChar))
        return 'part of a larger numeric expression';
    // A digit glued to letters is a code or a spinal level: L5, C5, T12, 3A.
    if (nextChar && /[A-Za-z]/.test(nextChar))
        return 'part of a code or level';
    if (prevChar && /[A-Za-z]/.test(prevChar))
        return 'part of a code or level';
    const before = wordBefore(text, start);
    if (LABEL_BEFORE.has(before))
        return `label following "${before}"`;
    const after = wordAfter(text, end);
    if (LABEL_AFTER.has(after))
        return `part of the expression "${after}"`;
    // A digit that opens a line followed by a full stop or bracket is a list label.
    const lineStart = text.lastIndexOf('\n', start - 1) + 1;
    if (text.slice(lineStart, start).trim() === '' && /^[.)\]]/.test(text.slice(end))) {
        return 'list numbering';
    }
    return null;
}
/**
 * Turn a run of number words into a value: "twenty three" is 23, "fifteen" is
 * 15. Returns null when the run is not a plain integer under 100.
 */
export function parseNumberWords(words) {
    const cleaned = words.map((w) => w.toLowerCase().replace(/[^a-z]/g, '')).filter(Boolean);
    if (cleaned.length === 0 || cleaned.length > 2)
        return null;
    if (cleaned.length === 1) {
        const v = WORD_VALUE.get(cleaned[0]);
        return v === undefined ? null : v;
    }
    const tens = TENS[cleaned[0]];
    const unit = WORD_VALUE.get(cleaned[1]);
    if (tens === undefined || unit === undefined || unit < 1 || unit > 9)
        return null;
    return tens + unit;
}
/** "seven and a half" becomes 7 with a vulgar half. Beam's guide asks for this. */
export function vulgarFraction(whole, fractionWord) {
    const glyph = FRACTION_WORDS[fractionWord.toLowerCase().trim()];
    if (!glyph)
        return null;
    return `${whole}${glyph}`;
}
export function numberToWord(n) {
    if (n < 1 || n > 9 || !Number.isInteger(n))
        return null;
    return UNITS_TO_NINETEEN[n] ?? null;
}
//# sourceMappingURL=numbers.js.map